import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import ThemeSwitch from '../components/ds/ThemeSwitch';
import {
  GraduationCap,
  Users,
  BookOpen,
  Shield,
  Globe,
  ArrowLeft,
  ArrowRight,
} from 'lucide-react';

/**
 * UnifiedLoginPage — Sovereign Tech Hub revision
 * ─────────────────────────────────────────────────────────────
 * Replaces the previous emerald/violet gradient role cards with
 * Soft Contrast Deep Navy (#0B1220) token-driven flat panels.
 *
 * Layout intent
 * • Full-height flex centered (no excessive top whitespace).
 * • 4 instrument-grade panels with razor-sharp 1px borders that
 *   light up indigo on hover.
 * • Single accent (Electric Blue) used only for the "Register"
 *   link to preserve a calm institutional rhythm.
 */
const ROLE_DEFS = [
  { id: 'student', icon: GraduationCap, titleAr: 'طالب',         titleEn: 'Student',        descAr: 'الدخول للحصص الدراسية والتعلم', descEn: 'Access lessons and learn',              path: '/login/student-auth' },
  { id: 'parent',  icon: Users,         titleAr: 'ولي أمر',       titleEn: 'Parent',         descAr: 'إدارة حسابات الأبناء والمتابعة', descEn: 'Manage children accounts & monitor',    path: '/login/parent-auth' },
  { id: 'teacher', icon: BookOpen,      titleAr: 'أستاذ',          titleEn: 'Teacher',        descAr: 'تقديم الدروس وإدارة الطلاب',     descEn: 'Teach lessons & manage students',       path: '/login/teacher-auth' },
  { id: 'admin',   icon: Shield,        titleAr: 'مسؤول المنصة',  titleEn: 'Platform Admin', descAr: 'إدارة المنصة والمستخدمين',         descEn: 'Manage platform & users',               path: '/login/admin-auth' },
];

const UnifiedLoginPage = () => {
  const navigate = useNavigate();
  const { language, toggleLanguage, localize } = useLanguage();
  const isAr = language === 'ar';
  const Arrow = isAr ? ArrowRight : ArrowLeft;

  /* Calm entrance — each card staggered by 80ms */
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);

  return (
    <div
      className="min-h-[100svh] flex items-center justify-center px-4 py-10 bg-[var(--bg-primary)] text-[var(--text-primary)]"
      dir={isAr ? 'rtl' : 'ltr'}
      data-testid="unified-login-page"
    >
      <div className="w-full max-w-3xl flex flex-col gap-10">
        {/* ── Top utilities ─────────────────────────────────── */}
        <div className="flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
            data-testid="login-back-home"
          >
            <Arrow className="w-4 h-4" />
            <span>{localize({ ar: 'الرئيسية', en: 'Home' })}</span>
          </Link>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={toggleLanguage}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-[var(--border-color)] hover:border-[var(--border-strong)] text-xs font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
              data-testid="login-lang-toggle"
            >
              <Globe className="w-3.5 h-3.5" />
              {isAr ? 'English' : 'العربية'}
            </button>
            <ThemeSwitch size="sm" data-testid="login-theme-switch" />
          </div>
        </div>

        {/* ── Brand head ─────────────────────────────────────── */}
        <div className="flex flex-col items-center text-center gap-4">
          <Link to="/" className="inline-flex items-center gap-3 group">
            <img
              loading="eager"
              src="/lwt-logo-new.png"
              alt="Learn With Trust"
              className="h-12 w-12 object-contain transition-transform group-hover:scale-[1.05] drop-shadow-[0_0_18px_rgba(99,102,241,0.35)]"
              style={{ background: 'transparent' }}
            />
            <div className={isAr ? 'text-right' : 'text-left'}>
              <span className="brand-wordmark text-xl text-[var(--accent-primary)]" data-testid="login-brand-wordmark">
                Learn With Trust
              </span>
              <span className="block text-xs text-[var(--text-tertiary)]">
                {localize({ ar: 'منصة تعلم مع ترست', en: 'Trusted Education Platform' })}
              </span>
            </div>
          </Link>

          <div className="space-y-1.5">
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">
              {localize({ ar: 'تسجيل الدخول', en: 'Sign In' })}
            </h1>
            <p className="text-sm text-[var(--text-secondary)]">
              {localize({ ar: 'اختر نوع حسابك للمتابعة', en: 'Choose your account type to continue' })}
            </p>
          </div>
        </div>

        {/* ── Role cards — Soft Contrast Deep Navy ───────────── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="login-role-grid">
          {ROLE_DEFS.map((role, i) => {
            const Icon = role.icon;
            return (
              <button
                key={role.id}
                type="button"
                onClick={() => navigate(role.path)}
                data-testid={`login-role-${role.id}`}
                style={{
                  transform: mounted ? 'translateY(0)' : 'translateY(8px)',
                  opacity: mounted ? 1 : 0,
                  transition: `transform 600ms cubic-bezier(0.16,1,0.3,1) ${i * 80}ms, opacity 600ms cubic-bezier(0.16,1,0.3,1) ${i * 80}ms, border-color 180ms ease, background-color 180ms ease`,
                }}
                className={[
                  /* Layout */
                  'group relative text-left',
                  isAr ? 'text-right' : 'text-left',
                  /* Surface — Soft Contrast Deep Navy on dark, soft gray on light */
                  'bg-[var(--bg-secondary)] hover:bg-[var(--bg-elevated)]',
                  'border border-[var(--border-color)] hover:border-[var(--accent-primary)]',
                  'rounded-xl px-5 py-4',
                  /* Focus ring */
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]',
                ].join(' ')}
              >
                <div className="flex items-center gap-4">
                  <div className="w-11 h-11 rounded-xl bg-[var(--bg-primary)] border border-[var(--border-color)] flex items-center justify-center shrink-0 transition-colors group-hover:border-[var(--accent-primary)]">
                    <Icon className="w-5 h-5 text-[var(--accent-primary)]" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-base font-semibold text-[var(--text-primary)] truncate">
                      {localize({ ar: role.titleAr, en: role.titleEn })}
                    </div>
                    <div className="text-xs text-[var(--text-secondary)] truncate">
                      {localize({ ar: role.descAr, en: role.descEn })}
                    </div>
                  </div>
                  <ArrowRight
                    className={`w-4 h-4 text-[var(--text-tertiary)] group-hover:text-[var(--accent-primary)] transition-all duration-200 group-hover:translate-x-0.5 ${isAr ? 'rotate-180 group-hover:-translate-x-0.5' : ''}`}
                  />
                </div>
              </button>
            );
          })}
        </div>

        {/* ── Footer links ───────────────────────────────────── */}
        <div className="flex flex-col items-center gap-3 text-center">
          <p className="text-sm text-[var(--text-secondary)]">
            {localize({ ar: 'ليس لديك حساب؟', en: "Don't have an account?" })}{' '}
            <Link
              to="/register"
              className="text-[var(--accent-primary)] hover:text-[var(--accent-primary-hover)] font-medium transition-colors"
              data-testid="login-register-link"
            >
              {localize({ ar: 'سجل الآن', en: 'Register Now' })}
            </Link>
          </p>
          <p className="text-[11px] text-[var(--text-tertiary)] tracking-wide">
            {localize({ ar: '© 2026 ترست اي دي تكنو للتطوير والابتكار ذ.م.م', en: '© 2026 Trust Ed Techno for Development and Innovation L.L.C.' })}
          </p>
        </div>
      </div>
    </div>
  );
};

export default UnifiedLoginPage;
